// Categorias do ERP (`raw.categoryTree` do dataset) como linhas do simulador. Usado pelo cenário por
// departamento (todas as categorias) e pela seção "Categorias fora da DRE" do cenário consolidado.

import { ancestorCodesOf, supportsMultiLevelCategories } from '../../../utils/categoryAncestors';

export const compareCode = (a, b) => String(a).localeCompare(String(b), 'pt-BR', { numeric: true });
const compareText = (a, b) => String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });

// Código estruturado (Omie/Excel: "1.01.02") dá a ordem do plano de contas; GUID (Conta Azul V2,
// Nibo) não diz nada: aí a ordem é pelo nome.
const STRUCTURED_CODE = /^\d+([.-]\d+)*$/;

/**
 * Comparador de itens { cod, label }: pelo código quando todos os códigos informados são
 * estruturados ("1.01.02"), senão pelo nome.
 */
export const categoryItemComparator = (cods) => {
  const structured = [...cods].every((cod) => STRUCTURED_CODE.test(cod));
  return (a, b) => (structured
    ? compareCode(a.cod, b.cod) || compareText(a.label, b.label)
    : compareText(a.label, b.label) || compareCode(a.cod, b.cod));
};

/**
 * Natureza da categoria: 'R' (receita), 'P' (despesa) ou null quando não informada. O backend já manda
 * normalizado (R/P); aceita também o texto do ERP (Conta Azul V2: RECEITA/DESPESA).
 */
export const parseCategoryNature = (natureza) => {
  const n = String(natureza || '').trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!n) return null;
  if (n === 'R' || n === 'E' || n.startsWith('RECEITA') || n.startsWith('ENTRADA') || n.startsWith('RECEB')) return 'R';
  if (n === 'P' || n === 'D' || n === 'S' || n.startsWith('DESPESA') || n.startsWith('SAIDA') || n.startsWith('CUSTO') || n.startsWith('PAG')) return 'P';
  return null;
};

/**
 * Categorias do ERP que viram linhas editáveis:
 * - as de baixo (sem filhas) ativas ou com realizado;
 * - uma categoria com filhas e com lançamento próprio também vira linha ("lançamentos diretos");
 * - categoria com realizado que não está no cadastro entra solta, pelo código.
 * Retorna { byCod: Map(cod -> cadastro), leaves: Map(cod -> { cod, descricao, groupCod, ancestors, nature, direct }) },
 * onde `groupCod` é a categoria superior direta (null quando não há) e `ancestors` a cadeia de
 * superiores do topo até `groupCod`. No Conta Azul (até 3 níveis) a cadeia é completa; nos demais
 * ERPs fica só a superior direta, como antes.
 */
export function collectErpLeaves(data) {
  const tree = (Array.isArray(data?.categoryTree) ? data.categoryTree : [])
    .map((c) => ({
      cod: String(c?.codCategoria ?? '').trim(),
      sup: String(c?.codCategoriaSup ?? '').trim() || null,
      descricao: String(c?.descricao || c?.codCategoria || '').trim(),
      natureza: c?.natureza ?? null,
      ativo: c?.ativo !== false,
    }))
    .filter((c) => c.cod);
  const byCod = new Map(tree.map((c) => [c.cod, c]));
  const values = Array.isArray(data?.values) ? data.values : [];
  const valued = new Set(values.map((v) => String(v?.codCategoria ?? '').trim()).filter(Boolean));
  const parents = new Set(tree.filter((c) => c.sup && byCod.has(c.sup) && c.sup !== c.cod).map((c) => c.sup));
  const multiLevel = supportsMultiLevelCategories(data?.sync?.erp);
  const parentByCod = new Map(tree.map((c) => [c.cod, c.sup]));
  // Superiores do topo até `groupCod` (inclusive).
  const ancestorsOf = (groupCod) => {
    if (!groupCod) return [];
    return multiLevel ? [...ancestorCodesOf(groupCod, parentByCod), groupCod] : [groupCod];
  };

  const leaves = new Map();
  const addLeaf = (cod, descricao, groupCod, natureza, direct = false) => {
    if (!leaves.has(cod)) {
      leaves.set(cod, { cod, descricao, groupCod, ancestors: ancestorsOf(groupCod), nature: parseCategoryNature(natureza), direct });
    }
  };
  tree.forEach((c) => {
    if (parents.has(c.cod)) {
      if (valued.has(c.cod)) addLeaf(c.cod, `${c.descricao} (lançamentos diretos)`, c.cod, c.natureza, true);
      return;
    }
    if (!c.ativo && !valued.has(c.cod)) return;
    addLeaf(c.cod, c.descricao, c.sup && byCod.has(c.sup) ? c.sup : null, c.natureza);
  });
  valued.forEach((cod) => { if (!byCod.has(cod)) addLeaf(cod, cod, null, null); });

  return { byCod, leaves };
}
